import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useNutsAuth } from '../../context/NutsAuthContext';
import { forumApi } from './forumApi';
import { PersonalAvatarCard } from '../community/PersonalAvatarCard';
import { usePolaroidUnread } from '../community/usePolaroidUnread';
import { useNavigate } from 'react-router-dom';

// Map dashboard polaroid keys → catalog category + URL slug
const POLAROID_ROUTING = {
  gifts:   { category: 'gifts',   slug: 'regals' },
  market:  { category: 'market',  slug: 'mercat' },
  private: { category: 'lessons', slug: 'classes-particulars' },
  // 'profe' intentionally not routed yet
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LOGO_URL =
  '/api/static/brand/logo-h0.png';

const LANGS = [
  { code: 'ca', label: 'CA' },
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

// Apps de la comunitat Nuts
const NUTS_APPS = [
  {
    key: 'classes',
    title: 'Classes UGOLF Aravell',
    description: { ca: 'Reserva les teves classes col·lectives a UGOLF Aravell.', es: 'Reserva tus clases colectivas en UGOLF Aravell.', en: 'Book your group classes at UGOLF Aravell.', fr: 'Réserve tes cours collectifs à UGOLF Aravell.' },
    url: 'https://golfclass-scheduler.emergent.host',
    primary: true,
    // No SSO — credentials are managed manually by Joan in Classes
  },
  {
    key: 'rules',
    title: 'Rules',
    description: { ca: "L'app per fer els tests oficials de regles i consultar un àrbitre virtual.", es: 'La app para tests oficiales de reglas y consultar un árbitro virtual.', en: 'Official rules tests and virtual referee.', fr: 'Tests officiels de règles et arbitre virtuel.' },
    url: 'https://golf-warmup-game.emergent.host',
  },
  // Mental — només es mostra si el backend retorna `mental_enabled: true` (vegeu MENTAL_APP_URL)
  {
    key: 'mental',
    title: 'Mental',
    description: { ca: 'Entrena la teva ment i millora el teu golf.', es: 'Entrena tu mente y mejora tu golf.', en: 'Train your mind and improve your golf.', fr: 'Entraîne ton mental et améliore ton golf.' },
    url: '#mental-sso', // overridden by SSO call
    sso: true,
    ssoEndpoint: '/api/nuts/sso/mental',
    requiresEnabled: 'mental',
  },
  {
    key: 'golflix',
    title: 'GOLFLIX',
    description: { ca: 'Contingut audiovisual de golf organitzat per categories.', es: 'Contenido audiovisual de golf organizado por categorías.', en: 'Golf audiovisual content by categories.', fr: 'Contenu audiovisuel de golf par catégories.' },
    url: 'https://golf-content-hub-1.emergent.host',
  },
];

// "Especials" — extres dins la comunitat Nuts (estètica Polaroid)
const NUTS_SPECIALS = [
  {
    key: 'gifts',
    emoji: '🎁',
    title: { ca: 'Regals i Premis', es: 'Regalos y Premios', en: 'Gifts & Prizes', fr: 'Cadeaux & Prix' },
    note: { ca: 'Sorpreses només per Nuts', es: 'Sorpresas solo para Nuts', en: 'Surprises only for Nuts', fr: 'Surprises pour Nuts' },
    tape: '#F4D77A',
    tilt: -2.5,
  },
  {
    key: 'market',
    emoji: '🛒',
    title: { ca: 'Mercat', es: 'Mercado', en: 'Marketplace', fr: 'Marché' },
    note: { ca: 'Nous i 2a mà', es: 'Nuevos y 2ª mano', en: 'New & 2nd hand', fr: 'Neufs & occasion' },
    tape: '#E8B4B4',
    tilt: 1.8,
  },
  {
    key: 'profe',
    emoji: '🧑‍🏫',
    title: { ca: 'Profe, què em passa?', es: 'Profe, ¿qué me pasa?', en: 'Coach, what\'s up?', fr: 'Prof, qu\'est-ce qui m\'arrive ?' },
    note: { ca: 'Consulta personal', es: 'Consulta personal', en: 'Personal consult', fr: 'Consultation perso' },
    tape: '#A8D5BA',
    tilt: -1.2,
  },
  {
    key: 'private',
    emoji: '⛳',
    title: { ca: 'Classes particulars', es: 'Clases particulares', en: 'Private lessons', fr: 'Cours particuliers' },
    note: { ca: 'Un a un, fes una cita', es: 'Uno a uno, pide cita', en: 'One-to-one, book a slot', fr: 'En tête-à-tête' },
    tape: '#B8C5E0',
    tilt: 2.2,
  },
];

export const NutsDashboard = () => {
  const { t, i18n } = useTranslation();
  const { member, logout, getToken, refreshMember } = useNutsAuth();
  const navigate = useNavigate();
  const unread = usePolaroidUnread('nuts', getToken);
  const lang = i18n.language || 'ca';
  const [showPin, setShowPin] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    forumApi.unread(token).then((d) => setUnreadCount(d.total || 0)).catch(() => {});
  }, [getToken]);

  const [config, setConfig] = useState({ mental_enabled: false, classes_enabled: true });
  useEffect(() => {
    axios.get(`${API_URL}/api/nuts/config`).then((r) => setConfig(r.data || {})).catch(() => {});
  }, []);

  const [openingSSO, setOpeningSSO] = useState(null); // 'classes' | 'mental' | null

  const openViaSSO = async (app) => {
    // iOS Safari blocks popups opened AFTER an async call. Open the window
    // synchronously (during the click) with a placeholder, then set its
    // location once the SSO URL arrives.
    const newWin = window.open('about:blank', '_blank');
    setOpeningSSO(app.key);
    try {
      const endpoint = app.ssoEndpoint || `/api/nuts/sso/${app.key}`;
      const r = await axios.get(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (newWin && !newWin.closed) {
        newWin.location = r.data.url;
      } else {
        window.location.href = r.data.url;
      }
    } catch (e) {
      if (newWin && !newWin.closed) newWin.close();
      toast.error(t('nuts.appOpenError', { app: app.title }));
    } finally {
      setOpeningSSO(null);
    }
  };

  const handleAppClick = (app, e) => {
    if (app.sso) {
      e.preventDefault();
      openViaSSO(app);
    }
  };

  // Filter apps by feature flags (e.g. Mental hidden until MENTAL_APP_URL is set)
  const visibleApps = NUTS_APPS.filter((app) => {
    if (app.requiresEnabled === 'mental' && !config.mental_enabled) return false;
    return true;
  });

  return (
    <div className="relative min-h-screen w-full bg-[#0E4D2A] text-[#F2E8D5] overflow-x-hidden" data-testid="nuts-dashboard">
      {/* Watermark */}
      <div className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center select-none">
        <span
          className="font-serif font-black text-[#083B1F]"
          style={{ fontSize: 'min(75vw, 75vh)', lineHeight: 1, opacity: 0.3 }}
          aria-hidden="true"
        >
          H0
        </span>
      </div>

      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-5 md:px-10 py-5">
        <Link
          to="/"
          data-testid="nuts-dash-back-h0"
          className="group flex items-center gap-2 font-sans text-[10px] md:text-xs tracking-[0.32em] text-[#A8B4A6] hover:text-[#D4AF37] transition-colors uppercase"
        >
          <span className="inline-block transition-transform group-hover:-translate-x-1">←</span>
          H0
        </Link>
        <div className="flex items-center gap-5">
          <Link
            to="/nuts/ajuda"
            data-testid="nuts-help-link"
            className="font-sans text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#A8B4A6] hover:text-[#D4AF37] transition-colors"
          >
            ❓ {t('nuts.help', { defaultValue: 'Ajuda' })}
          </Link>
          <div className="hidden md:flex gap-4 font-sans text-[11px] tracking-[0.25em]">
            {LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => i18n.changeLanguage(l.code)}
                className={`transition-colors duration-300 ${
                  lang === l.code ? 'text-[#D4AF37]' : 'text-[#A8B4A6] hover:text-[#F2E8D5]'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={logout}
            data-testid="nuts-logout-btn"
            className="font-sans text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#A8B4A6] hover:text-[#D4AF37] transition-colors"
          >
            {t('nuts.logout')}
          </button>
        </div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-10 pt-24 md:pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="flex flex-col items-center text-center"
        >
          <img src={LOGO_URL} alt="Hoyo Zero" className="w-16 sm:w-20 h-auto" />
          <p className="mt-6 text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px] sm:text-xs">
            🥜 NUTS · ANDORRA
          </p>
        </motion.div>

        {/* Member welcome card */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-10 flex flex-col items-center text-center"
          data-testid="nuts-welcome-card"
        >
          {(() => {
            const useGenerated = member?.personal_avatar_status === 'approved' && member?.personal_avatar_url;
            const personal = member?.personal_avatar_url;
            const fallback = member?.avatar_url;
            const initialSrc = useGenerated ? personal : fallback;
            if (!initialSrc) return null;
            return (
              <img
                src={initialSrc}
                alt={member.nickname}
                onError={(e) => {
                  if (fallback && e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
                }}
                className="w-24 h-24 rounded-full border-2 border-[#D4AF37] bg-[#083B1F] shadow-[0_8px_30px_rgba(212,175,55,0.2)] object-cover"
                data-testid="nuts-member-avatar"
              />
            );
          })()}
          <h1 className="mt-6 font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight" data-testid="nuts-member-nickname">
            {t('nuts.hello', { name: member?.nickname || '' })}
          </h1>
          <p className="mt-3 font-serif italic text-[#A8B4A6] text-base md:text-lg max-w-xl">
            {t('nuts.dashboardSubtitle')}
          </p>
        </motion.div>

        {/* PIN card removed — Classes credentials now managed manually by Joan.
            The PIN is still stored in the DB and used silently by Mental SSO. */}

        <PersonalAvatarCard community="nuts" getToken={getToken} lang={i18n.language} onApproved={refreshMember} />

        {/* Forum card with unread badge */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="mt-8"
        >
          <Link
            to="/nuts/forum"
            data-testid="nuts-forum-link"
            className="group relative block rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 bg-gradient-to-br from-[#083B1F]/60 to-[#0E4D2A]/30 backdrop-blur-sm p-5 md:p-6 transition-all duration-500"
          >
            <div className="flex items-center gap-5">
              <span className="text-3xl md:text-4xl flex-shrink-0">💬</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif text-xl md:text-2xl tracking-tight">{t('nuts.forumCardTitle')}</h3>
                  {unreadCount > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-2 rounded-full bg-red-600 text-white text-[11px] font-sans font-medium tabular-nums shadow-[0_0_12px_rgba(220,38,38,0.5)]"
                      data-testid="nuts-forum-badge"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
                <p className="mt-1 font-sans font-light text-[#A8B4A6] text-sm">{t('nuts.forumCardSubtitle')}</p>
              </div>
              <span className="text-[#D4AF37] text-2xl group-hover:translate-x-1 transition-transform">→</span>
            </div>
          </Link>
        </motion.div>

        {/* Apps grid */}
        <div className="mt-16">
          <h2 className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#A8B4A6] text-center">
            ✦ {t('nuts.toolsTitle')}
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-4 md:gap-6">
            {visibleApps.map((app, i) => (
              <motion.a
                key={app.key}
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => handleAppClick(app, e)}
                data-testid={`nuts-app-${app.key}`}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + i * 0.1 }}
                className={`group relative block rounded-2xl border p-6 md:p-8 transition-all duration-500 overflow-hidden ${
                  app.primary
                    ? 'border-[#D4AF37]/50 bg-gradient-to-br from-[#D4AF37]/15 to-[#083B1F]/30 hover:border-[#D4AF37]'
                    : 'border-[#D4AF37]/15 bg-[#083B1F]/30 hover:border-[#D4AF37]/40'
                } ${app.sso && openingSSO === app.key ? 'opacity-60 pointer-events-none' : ''}`}
              >
                {app.primary && (
                  <span className="absolute top-4 right-4 font-sans text-[9px] tracking-[0.32em] uppercase text-[#D4AF37]">
                    ★ {t('nuts.recommended')}
                  </span>
                )}
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight">
                  {app.title}
                </h3>
                <p className="mt-3 font-sans font-light text-[#F2E8D5]/80 leading-relaxed text-sm md:text-base">
                  {app.description[lang] || app.description.ca}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] group-hover:gap-3 transition-all">
                  {app.sso ? (openingSSO === app.key ? t('nuts.openingSSO') : t('nuts.openSSO')) : t('nuts.open')}
                  <span>↗</span>
                </span>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Especials — estètica Polaroid */}
        <div className="mt-20" data-testid="nuts-specials">
          <h2 className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#A8B4A6] text-center">
            ✦ {t('nuts.specialsTitle')}
          </h2>
          <p className="mt-2 text-center font-serif italic text-[#A8B4A6] text-sm">
            {t('nuts.specialsSubtitle')}
          </p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 px-2">
            {NUTS_SPECIALS.map((sp, i) => {
              const route = POLAROID_ROUTING[sp.key];
              const unreadCount = route ? (unread[route.category] || 0) : 0;
              const handleClick = () => {
                if (route) {
                  navigate(`/nuts/${route.slug}`);
                } else {
                  toast(t('nuts.specialsComingSoon'), { description: sp.title[lang] || sp.title.ca });
                }
              };
              return (
              <motion.button
                key={sp.key}
                type="button"
                data-testid={`nuts-special-${sp.key}`}
                onClick={handleClick}
                initial={{ opacity: 0, y: 30, rotate: 0 }}
                animate={{ opacity: 1, y: 0, rotate: sp.tilt }}
                whileHover={{ rotate: 0, y: -6, scale: 1.03 }}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.08 }}
                className="group relative block bg-[#FBF7EE] text-[#1a1a1a] rounded-[3px] pt-3 px-3 pb-5 shadow-[0_10px_30px_rgba(0,0,0,0.35),0_2px_6px_rgba(0,0,0,0.2)] hover:shadow-[0_18px_40px_rgba(0,0,0,0.45),0_4px_10px_rgba(0,0,0,0.25)] transition-shadow duration-500 cursor-pointer"
                style={{ transformOrigin: 'center top' }}
              >
                {/* Adhesive tape */}
                <span
                  className="absolute -top-2 left-1/2 -translate-x-1/2 w-14 h-5 rounded-[2px] opacity-80 shadow-sm"
                  style={{
                    backgroundColor: sp.tape,
                    backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(0,0,0,0.05) 100%)',
                  }}
                  aria-hidden="true"
                />
                {/* "Photo" area */}
                <div className="aspect-square w-full bg-gradient-to-br from-[#0E4D2A] to-[#083B1F] rounded-[2px] flex items-center justify-center overflow-hidden">
                  <span className="text-5xl md:text-6xl select-none filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                    {sp.emoji}
                  </span>
                </div>
                {/* Handwritten caption */}
                <div className="mt-3 text-center px-1">
                  <p
                    className="leading-tight text-[#1a1a1a]"
                    style={{ fontFamily: "'Caveat', cursive", fontSize: '1.35rem', fontWeight: 600 }}
                  >
                    {sp.title[lang] || sp.title.ca}
                  </p>
                  <p
                    className="text-[#666] mt-0.5"
                    style={{ fontFamily: "'Caveat', cursive", fontSize: '0.95rem' }}
                  >
                    {sp.note[lang] || sp.note.ca}
                  </p>
                </div>
                {/* "Coming soon" stamp */}
                {!route && (
                  <span
                    className="absolute -bottom-2 -right-2 rotate-[-12deg] bg-[#8B2C2C]/90 text-white text-[8px] tracking-[0.3em] uppercase px-2 py-0.5 rounded-sm font-sans shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    {t('nuts.specialsSoon')}
                  </span>
                )}
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-3 -right-3 min-w-[28px] h-7 px-1.5 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center shadow-lg ring-2 ring-[#FBF7EE] animate-pulse"
                    data-testid={`nuts-special-${sp.key}-unread`}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </motion.button>
              );
            })}
          </div>
        </div>

        {/* Footer signature */}
        <p className="mt-16 text-center text-[10px] tracking-[0.3em] uppercase text-[#A8B4A6]/60 font-sans">
          © {new Date().getFullYear()} · Hoyo Zero · @joan_monzo
        </p>
      </div>
    </div>
  );
};

export default NutsDashboard;
